import { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import styles from './ExperimentForm.module.css';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { AvlTextField } from '../../Shared/Inputs/FieldText/AvlTextField';

export const FieldKeys = {
    name: 'name',
};

const defaultValues = {
    [FieldKeys.name]: '',
};

const FieldOptions = {
    [FieldKeys.name]: {
        label: 'Experiment Name',
        isRequired: true,
        type: 'text',
    },
};

function ExperimentForm(
    {
        onSave,
        initialValues = defaultValues,
        onDirtyChange,
    },
) {
    const formRef = useRef(null);
    const timeoutRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);
    const validationSchema = yup.object({
        [FieldKeys.name]: yup
            .string('Enter experiment name (str)')
            .required('Experiment name is required'),
    });

    const formik = useFormik({
        initialValues: {
            ...defaultValues,
            ...initialValues,
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setIsSaving(true);

            try {
                await onSave?.(values);
                formik.resetForm({ values });
            } finally {
                setIsSaving(false);
            }
        },
    });

    const textField = (fieldKey) => {
        const params = FieldOptions[fieldKey];
        const isError = formik.touched[fieldKey] ? !!formik.errors[fieldKey] : false;
        const helperText = formik.touched[fieldKey] && formik.errors[fieldKey];

        return (
            <AvlTextField
                className={styles.field}
                label={params.label}
                name={fieldKey}
                type={params.type}
                value={formik.values[fieldKey] ?? ''}
                onChange={formik.handleChange}
                error={isError}
                helperText={helperText}
            />
        );
    };

    useEffect(() => {
        onDirtyChange?.(formik.dirty);
    }, [formik.dirty, onDirtyChange]);

    useEffect(() => {
        const handleFocusOut = (e) => {
            if (!formRef.current?.contains(e.relatedTarget)) {
                if (formik.dirty && formik.isValid) {
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }

                    timeoutRef.current = setTimeout(() => {
                        formik.submitForm()
                            .catch(console.error);
                    }, 500);
                }
            }
        };

        const formElement = formRef.current;
        formElement?.addEventListener('focusout', handleFocusOut);

        return () => {
            formElement?.removeEventListener('focusout', handleFocusOut);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [formik]);

    return (
        <div className={styles.form} ref={formRef} tabIndex={-1}>
            {textField(FieldKeys.name)}
            <div className={styles.controlsContainer}>
                <Button
                    className="button"
                    variant="contained"
                    size="small"
                    color="primary"
                    disabled={!formik.isValid || !formik.dirty || isSaving}
                    onClick={() => {
                        formik.submitForm()
                            .catch(console.error);
                    }}
                >
                    {isSaving
                        ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <CircularProgress size={16} color="inherit" />
                                Saving...
                            </span>
                        )
                        : 'Save'}
                </Button>
            </div>
        </div>
    );
}

export default ExperimentForm;
